import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAsync } from '../../utils/useAsync';
import { DriverFormDataSubmit, useDriverFormService } from '../../services/driverFormService';
import { useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { InferType } from 'yup';
import { useNavigate } from 'react-router-dom';

export const useDriverFormController = () => {

    const currentYear = new Date().getFullYear();
    const minAllowedYear = currentYear - 25;

    const eighteenYearsAgo = new Date();
    eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);

    const fileSchema = yup.object({
        url: yup.string().required("La URL de la imagen es requerida"),
        name: yup.string().required("El nombre del archivo es requerido"),
    });

    const driverSchema = yup.object().shape({
        // Driver information
        firstName: yup.string().required('El nombre es requerido')
            .max(25, 'El nombre debe tener menos de 25 caracteres'),
        lastName: yup.string().required('El apellido es requerido')
            .max(25, 'El apellido debe tener menos de 25 caracteres'),
        dni: yup.string().required('El DNI es requerido')
            .length(8, 'El DNI debe tener 8 caracteres')
            .matches(/^[0-9]+$/, 'El DNI debe contener solo números')
        ,
        phone: yup.string().required('El teléfono es requerido')
            .length(10, 'El teléfono debe tener 10 caracteres')
            .matches(/^[0-9]+$/, 'El teléfono debe contener solo números')
        ,
        state: yup.string().required('La provincia es requerida'),
        city: yup.string().required('La localidad es requerida'),
        email: yup.string().email('Email inválido').required('El email es requerido')
            .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'El email debe ser válido'),
        birthDate: yup.date()
            .required('La fecha de nacimiento es requerida')
            .max(eighteenYearsAgo, 'Debes ser mayor de 18 años para registrarte')
            .typeError('Fecha de nacimiento inválida'),
        gender: yup.string().required('El género es requerido'),


        // Vehicle information
        licensePlate: yup.string().required('La patente es requerida'),
        vehicleType: yup.string().required('El tipo de vehículo es requerido'),
        brand: yup.string().required('La marca es requerida'),
        model: yup.string().required('El modelo es requerido'),
        color: yup.string().required('El color es requerido'),
        year: yup.number()
            .typeError('El año debe ser un número')
            .min(minAllowedYear, `El año debe ser mayor o igual a ${minAllowedYear}`)
            .max(currentYear, `El año debe ser menor o igual a ${currentYear}`)
            .required("El año es requerido"),

        // Files
        profilePhoto:
            yup.mixed()
                .required('La foto de perfil es requerida')
                .test(
                    'fileRequired',
                    'Debes subir un archivo',
                    (value) => value instanceof File && value.size > 0
                ),
        dniFrontPhoto:
            yup.mixed()
                .required('La foto frontal del DNI es requerida')
                .test(
                    'fileRequired',
                    'Debes subir un archivo',
                    (value) => value instanceof File && value.size > 0
                ),
        dniBackPhoto:
            yup.mixed()
                .required('La foto trasera del DNI es requerida')
                .test(
                    'fileRequired',
                    'Debes subir un archivo',
                    (value) => value instanceof File && value.size > 0
                ),
        licenseFrontPhoto:
            yup.mixed()
                .required('La foto frontal de la licencia es requerida')
                .test(
                    'fileRequired',
                    'Debes subir un archivo',
                    (value) => value instanceof File && value.size > 0
                ),
        licenseBackPhoto:
            yup.mixed()
                .required('La foto trasera de la licencia es requerida')
                .test(
                    'fileRequired',
                    'Debes subir un archivo',
                    (value) => value instanceof File && value.size > 0
                ),
        vehicleRegistrationFrontPhoto:
            yup.mixed()
                .required('La foto frontal de la cédula es requerida')
                .test(
                    'fileRequired',
                    'Debes subir un archivo',
                    (value) => value instanceof File && value.size > 0
                ),
        vehicleRegistrationBackPhoto:
            yup.mixed()
                .required('La foto trasera de la cédula es requerida')
                .test(
                    'fileRequired',
                    'Debes subir un archivo',
                    (value) => value instanceof File && value.size > 0
                ),

        // Files URLs 
        profilePhotoUrl: fileSchema.required("La foto de perfil es requerida"),
        dniFrontPhotoUrl: fileSchema.required("La foto frontal del DNI es requerida"),
        dniBackPhotoUrl: fileSchema.required("La foto trasera del DNI es requerida"),
        licenseFrontPhotoUrl: fileSchema.required("La foto frontal de la licencia es requerida"),
        licenseBackPhotoUrl: fileSchema.required("La foto trasera de la licencia es requerida"),
        vehicleRegistrationFrontPhotoUrl: fileSchema.required("La foto frontal de la cédula es requerida"),
        vehicleRegistrationBackPhotoUrl: fileSchema.required("La foto trasera de la cédula es requerida"),
    });



    type DriverFormData = yup.InferType<typeof driverSchema>;

        const toast = useToast();

        const {
            register,
            handleSubmit,
            formState: { errors, isSubmitting },
            setValue,
            getValues,
            reset,
            setError,
            trigger,
            clearErrors,
            watch,
        } = useForm<DriverFormData>({
            resolver: yupResolver(driverSchema),
            mode: 'onChange',
        });

        type RawDriverFormData = InferType<typeof driverSchema>;

        const mapToDriverFormData = (form: RawDriverFormData): DriverFormDataSubmit => {
            const documents: Array<{name: DocumentType, url: string}> = [
                { name: 'document-front', url: form.dniFrontPhotoUrl.url },
                { name: 'document-back', url: form.dniBackPhotoUrl.url },
                { name: 'license-front', url: form.licenseFrontPhotoUrl.url },
                { name: 'license-back', url: form.licenseBackPhotoUrl.url },
                { name: 'cedula-front', url: form.vehicleRegistrationFrontPhotoUrl.url },
                { name: 'cedula-back', url: form.vehicleRegistrationBackPhotoUrl.url },
            ];

            return {
                userData: {
                    email: `${form.dni}@viajaya.app`,
                    userEmail: form.email,
                    password: '',
                    profile: {
                        firstname: form.firstName,
                        lastname: form.lastName,
                        location: {
                            province: form.state,
                            city: form.city,
                        },
                        gender: form.gender as 'male' | 'female' | 'other',
                        photoUrl: form.profilePhotoUrl.url,
                        birthDate: new Date(form.birthDate).toISOString(),
                        billingData: {
                            documentNumber: form.dni,
                            phoneNumber: form.phone,
                        },
                    },
                    documents: documents
                },
                vehicleData: {
                    brand: form.brand,
                    model: form.model,
                    year: form.year,
                    color: form.color,
                    patent: form.licensePlate,
                    type: form.vehicleType,
                }
            };
        };


        const onSubmit = async (formData: RawDriverFormData) => {
            try {
                const isValid = await trigger();
                if (!isValid) return;
                const data = mapToDriverFormData(formData);
                executeSubmitDriverForm(data);
            } catch (error) {
                console.error('Error submitting form:', error);
            }
        };


        const [uploadingFiles, setUploadingFiles] = useState<Record<string, boolean>>({});
        const navigate = useNavigate();


        const { validateDriver, validateVehicle, uploadFile, submitDriverForm } = useDriverFormService();

        const [, , validateVehicleLoading, executeValidateVehicle] = useAsync(validateVehicle, {
            onSuccess(data) {
                if (data.data !== null) {
                    setError("licensePlate", {
                        type: "manual",
                        message: "Patente ya registrada",
                    });
                }
            },
        });

        const [, , validateDriverLoading, executeValidateDriver] = useAsync(validateDriver, {
            onSuccess(data) {
                if (data.exist) {
                    setError("dni", {
                        type: "manual",
                        message: "DNI ya registrado",
                    });
                }
            },
        });

        const [, , , executeSubmitDriverForm] = useAsync(submitDriverForm, {
            onSuccess() {
                toast({
                    title: 'Gracias por registrarte en ViajáYa',
                    description: 'Estamos revisando tu solicitud. Próximamente recibirás un correo eléctrónico con nuestra respuesta',
                    status: 'success',
                    duration: 15000,
                    isClosable: true,
                    position: "top-right"
                });
                reset();
                setTimeout(() => {
                    navigate("/");
                }, 15000);
            },
            onError(error) {
                let errorMessage = "Ha ocurrido un error al registrar el conductor";
                if (error.message === "User already exists") {
                    errorMessage = "El conductor se encuentra registrado";
                }
                toast({
                    title: 'Error',
                    description: errorMessage,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                    position: "top-right"
                });
            }
        });

        
        type DocumentType = 'license-front' | 'cedula-front' | 'document-front' | 'document-back' | 'license-back' | 'cedula-back';
        
        const [, , loadingUploadFile, executeUploadFile] = useAsync(uploadFile, {
            onSuccess(data) {
                setUploadingFiles(prev => ({
                    ...prev,
                    [data.fileName]: false
                }));

                
                switch (data.fileName) {
                    case "dniFrontPhoto":
                        setValue("dniFrontPhotoUrl", { url: data.url, name: data.fileName });
                        break;
                    case "dniBackPhoto":
                        setValue("dniBackPhotoUrl", { url: data.url, name: data.fileName });
                        break;
                    case "licenseFrontPhoto":
                        setValue("licenseFrontPhotoUrl", { url: data.url, name: data.fileName });
                        break;
                    case "licenseBackPhoto":
                        setValue("licenseBackPhotoUrl", { url: data.url, name: data.fileName });
                        break;
                    case "vehicleRegistrationFrontPhoto":
                        setValue("vehicleRegistrationFrontPhotoUrl", { url: data.url, name: data.fileName });
                        break;
                    case "vehicleRegistrationBackPhoto":
                        setValue("vehicleRegistrationBackPhotoUrl", { url: data.url, name: data.fileName });
                        break;
                    case "profilePhoto":
                        setValue("profilePhotoUrl", { url: data.url, name: data.fileName });
                        break;
                }
            },
        });

        const onBlurLicensePlate = () => {
            executeValidateVehicle(getValues("licensePlate"));
        };

        const onBlurDni = () => {
            executeValidateDriver(getValues("dni"));
        };



        return {
            handleSubmit,
            onSubmit,
            onBlurLicensePlate,
            onBlurDni,
            errors,
            register,
            getValues,
            trigger,
            setValue,
            setError,
            reset,
            watch,
            validateDriverLoading,
            validateVehicleLoading,
            eighteenYearsAgo,
            executeUploadFile,
            loadingUploadFile,
            uploadingFiles,
            isSubmitting,
            setUploadingFiles,
            clearErrors,
        }
    }

